'use client';
import React, { useRef, useState } from 'react';

const BuildingIcon = () => (
	<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-8 w-8 text-white">
		<path d="M6 22V4a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v18Z"></path>
		<path d="M6 12H4a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2h2"></path>
		<path d="M18 9h2a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2h-2"></path>
		<path d="M10 6h4"></path>
		<path d="M10 10h4"></path>
		<path d="M10 14h4"></path>
		<path d="M10 18h4"></path>
	</svg>
);

const UserIcon = () => (
	<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-8 w-8 text-white">
		<path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2"></path>
		<circle cx="12" cy="7" r="4"></circle>
	</svg>
);

const ChevronIcon = () => (
	<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
		<path d="m6 9 6 6 6-6"></path>
	</svg>
);

type AccordionItemProps = {
	value: string;
	title: string;
	children: React.ReactNode;
	isOpen: boolean;
	onToggle: () => void;
};

const AccordionItem = ({ value, title, children, isOpen, onToggle }: AccordionItemProps) => {
	const contentRef = useRef<HTMLDivElement>(null);
	const contentHeight = isOpen ? `${contentRef.current?.scrollHeight ?? 0}px` : '0px';
	const contentId = `home-faq-content-${value}`;
	const triggerId = `home-faq-trigger-${value}`;

	return (
		<div className="overflow-hidden rounded-[14px] border border-[#eef2f7] bg-white shadow-[0_6px_18px_rgba(15,23,42,0.05)] transition-all duration-300 hover:-translate-y-[1px] hover:shadow-[0_12px_26px_rgba(15,25,215,0.12)]">
			<button
				id={triggerId}
				type="button"
				aria-expanded={isOpen}
				aria-controls={contentId}
				onClick={onToggle}
				className="group flex w-full items-center justify-between gap-6 px-5 py-[18px] text-left outline-none [-webkit-tap-highlight-color:transparent] active:bg-white"
			>
				<span
					className={`text-[15px] font-bold leading-[1.35] transition-colors duration-300 ${
						isOpen ? 'text-brand-main' : 'text-[#0f172a] group-hover:text-brand-main'
					}`}
				>
					{title}
				</span>
				<span
					className={`shrink-0 transition-all duration-300 ${
						isOpen ? 'rotate-180 text-brand-main' : 'text-[#334155] group-hover:text-brand-main'
					}`}
				>
					<ChevronIcon />
				</span>
			</button>

			<div
				id={contentId}
				ref={contentRef}
				role="region"
				aria-labelledby={triggerId}
				style={{ maxHeight: contentHeight }}
				className={`overflow-hidden transition-[max-height,opacity] duration-500 ease-[cubic-bezier(0.16,1,0.3,1)] ${
					isOpen ? 'opacity-100' : 'opacity-0'
				}`}
			>
				<div className="px-5 pb-5">
					<p className="border-t border-[#f1f5f9] pt-4 text-left text-[14px] leading-[1.55] text-[#475569]">
						{children}
					</p>
				</div>
			</div>
		</div>
	);
};

function FAQ() {
	const [openCompanyItemId, setOpenCompanyItemId] = useState<string | null>(null);
	const [openCandidateItemId, setOpenCandidateItemId] = useState<string | null>(null);

	return (
		<section id="faq" className="w-full bg-[#F3F4F6] py-32 px-4 shadow-inner">
			<div className="max-w-[1140px] mx-auto">
				<h2 className="mb-12 text-center font-bold text-[#111827] text-[36px] lg:text-[42px] tracking-tight">
					Perguntas Frequentes
				</h2>

				<div className="grid grid-cols-1 md:grid-cols-2 gap-12 lg:gap-16">
					{/* Empresas Section */}
					<div className="flex flex-col gap-8">
						<div className="flex items-center gap-6 mb-4">
							<div className="min-w-[48px] min-h-[48px] w-12 h-12 rounded-full bg-brand-main flex items-center justify-center shadow-md shadow-brand-main/25 flex-shrink-0">
								<BuildingIcon />
							</div>
							<h3 className="font-bold text-[#111827] text-[24px] mb-0">
								Principais dúvidas das empresas
							</h3>
						</div>

						<div className="w-full space-y-4">
							<AccordionItem
								value="emp-1"
								title="O que é a plataforma IncluiPcD?"
								isOpen={openCompanyItemId === 'emp-1'}
								onToggle={() => setOpenCompanyItemId((currentId) => (currentId === 'emp-1' ? null : 'emp-1'))}
							>
								A IncluiPcD é uma plataforma contínua que conecta empresas comprometidas com a diversidade a profissionais com deficiência, oferecendo acesso a talentos qualificados e conteúdos exclusivos ao longo de todo o ano.
							</AccordionItem>
							<AccordionItem
								value="emp-2"
								title="Como posso cadastrar minha empresa e publicar vagas?"
								isOpen={openCompanyItemId === 'emp-2'}
								onToggle={() => setOpenCompanyItemId((currentId) => (currentId === 'emp-2' ? null : 'emp-2'))}
							>
								Você pode se cadastrar na plataforma escolhendo um dos planos disponíveis. Cada plano inclui diferentes benefícios, como detalhamento de vagas, acesso a candidatos, integração com ATS e divulgação em redes sociais, WhatsApp e e-mail. Assim, sua empresa passa a aparecer para profissionais que buscam oportunidades com visibilidade contínua.
							</AccordionItem>
							<AccordionItem
								value="emp-3"
								title="Como acompanhar os candidatos que se inscrevem nas minhas vagas?"
								isOpen={openCompanyItemId === 'emp-3'}
								onToggle={() => setOpenCompanyItemId((currentId) => (currentId === 'emp-3' ? null : 'emp-3'))}
							>
								A plataforma oferece dashboards intuitivos, onde você pode visualizar perfis, gerenciar candidaturas e se comunicar diretamente com os candidatos.
							</AccordionItem>
							<AccordionItem
								value="emp-4"
								title="A plataforma funciona apenas durante o evento?"
								isOpen={openCompanyItemId === 'emp-4'}
								onToggle={() => setOpenCompanyItemId((currentId) => (currentId === 'emp-4' ? null : 'emp-4'))}
							>
								Não. A IncluiPcD é ativa 365 dias por ano, conectando talentos e empresas continuamente.
							</AccordionItem>
						</div>
					</div>

					{/* Candidatos Section */}
					<div className="flex flex-col gap-8">
						<div className="flex items-center gap-6 mb-4">
							<div className="min-w-[48px] min-h-[48px] w-12 h-12 rounded-full bg-brand-main flex items-center justify-center shadow-md shadow-brand-main/25 flex-shrink-0">
								<UserIcon />
							</div>
							<h3 className="font-bold text-[#111827] text-[24px] mb-0">
								Principais dúvidas dos candidatos
							</h3>
						</div>

						<div className="w-full space-y-4">
							<AccordionItem
								value="cand-1"
								title="Como me inscrevo na plataforma?"
								isOpen={openCandidateItemId === 'cand-1'}
								onToggle={() => setOpenCandidateItemId((currentId) => (currentId === 'cand-1' ? null : 'cand-1'))}
							>
								Crie seu perfil gratuito diretamente na plataforma, informando seus dados e experiência profissional.
							</AccordionItem>
							<AccordionItem
								value="cand-2"
								title="Como encontro vagas adequadas ao meu perfil?"
								isOpen={openCandidateItemId === 'cand-2'}
								onToggle={() => setOpenCandidateItemId((currentId) => (currentId === 'cand-2' ? null : 'cand-2'))}
							>
								Use o buscador e filtros por área, localidade e tipo de vaga. A plataforma recomenda oportunidades relevantes com base no seu perfil.
							</AccordionItem>
							<AccordionItem
								value="cand-3"
								title="Posso me candidatar a vagas de diferentes empresas?"
								isOpen={openCandidateItemId === 'cand-3'}
								onToggle={() => setOpenCandidateItemId((currentId) => (currentId === 'cand-3' ? null : 'cand-3'))}
							>
								Sim! Você pode se candidatar a quantas vagas quiser, diretamente pela plataforma.
							</AccordionItem>
							<AccordionItem
								value="cand-4"
								title="Existe algum custo para candidatos?"
								isOpen={openCandidateItemId === 'cand-4'}
								onToggle={() => setOpenCandidateItemId((currentId) => (currentId === 'cand-4' ? null : 'cand-4'))}
							>
								Não. O cadastro e o acesso às vagas e conteúdos são gratuitos.
							</AccordionItem>
						</div>
					</div>
				</div>
			</div>
		</section>
	);
}

export default FAQ;